import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
  ActionTrackMetadata,
} from '@cotera/client/v0/actions/framework';
import type { ActionTrackingContext } from '@cotera/client/v0/actions/framework/action-tracking';
import { err, ok } from 'neverthrow';
import { CocoChatModel } from '../models/coco-chat.model';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import {
  CocoEvent,
  cocoActionMetadata,
  getChatSurface,
} from '@cotera/client/app/etc';

export class UnfocusAgentAction extends BaseAction<
  Record<string, never>,
  { t: string; message?: string }[]
> {
  title = 'Unfocus Agent';
  inputSchema = z.object({});
  group = 'Chat';
  description = 'Stop prioritizing a specific agent';

  constructor(private readonly client: TenantedClient['resource']['chat']) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    const model = context.targetedModelOfType(CocoChatModel);
    return (
      model !== null &&
      model.conversationWatchable.snapshot() !== null &&
      (model.conversationWatchable.snapshot()?.hasFocusedAgent() ?? false)
    );
  }

  async execute(
    _: Record<string, never>,
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return err([{ t: 'error', message: 'Coco chat model not found' }]);
    }

    const conversation = model.conversationWatchable.snapshot();
    if (conversation === null) {
      return err([{ t: 'error', message: 'Conversation not found' }]);
    }

    const updatedConversation = conversation.clone();
    updatedConversation.unfocusAgent();

    model.conversationWatchable.set(updatedConversation);

    const result = await this.client.addAction({
      conversationId: conversation.getId(),
      action: { type: 'unfocus_agent' },
    });

    if (result.isErr()) {
      const errorMessage =
        result.error.t === 'application'
          ? result.error.err.message
          : 'Failed to unfocus agent';
      return err([{ t: 'error', message: errorMessage }]);
    }

    return ok({});
  }

  track({
    context,
  }: ActionTrackingContext<
    Record<string, never>,
    { t: string; message?: string }[]
  >): ActionTrackMetadata | null {
    const model = context.targetedModelOfType(CocoChatModel);
    if (model === null) {
      return null;
    }

    return cocoActionMetadata(CocoEvent.AgentUnfocused, {
      conversationId: model.conversationId,
      surface: getChatSurface(context),
    });
  }
}
